export interface PushApi {
  getVapidPublicKey(): Promise<string>;
  subscribe(subscription: PushSubscription): Promise<void>;
  unsubscribe(endpoint: string): Promise<void>;
}

export class WebPushService {
  private pushApi: PushApi;
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  constructor(pushApi: PushApi) {
    this.pushApi = pushApi;
  }

  /**
   * Service Worker를 등록하고 기존 구독을 확인.
   * 이미 등록된 ServiceWorkerRegistration이 있으면 전달하여 재등록을 방지할 수 있다.
   */
  async init(
    existingRegistration?: ServiceWorkerRegistration,
  ): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn("Push notifications are not supported in this browser");
      return false;
    }

    try {
      if (existingRegistration) {
        this.registration = existingRegistration;
      } else {
        this.registration = await navigator.serviceWorker.register("/sw.js");
      }
      console.log("Service Worker registered:", this.registration);

      // Check for existing subscription
      this.subscription = await this.registration.pushManager.getSubscription();
      return true;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
      return false;
    }
  }

  isSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  async getPermissionState(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      return "denied";
    }
    return Notification.permission;
  }

  isSubscribed(): boolean {
    return this.subscription !== null;
  }

  getSubscription(): PushSubscription | null {
    return this.subscription;
  }

  async subscribe(): Promise<PushSubscription | null> {
    if (!this.registration) {
      throw new Error("Service Worker not registered");
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Notification permission denied");
    }

    // Get VAPID public key from server
    const vapidPublicKey = await this.pushApi.getVapidPublicKey();
    const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

    // Subscribe to push notifications
    this.subscription = await this.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    // Send subscription to server
    await this.pushApi.subscribe(this.subscription);

    return this.subscription;
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const endpoint = this.subscription.endpoint;

    // Unsubscribe from push manager
    const success = await this.subscription.unsubscribe();
    if (success) {
      // Remove subscription from server
      await this.pushApi.unsubscribe(endpoint);
      this.subscription = null;
    }

    return success;
  }

  private urlBase64ToUint8Array(base64String: string): BufferSource {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
